import { useCallback, useRef, useState } from 'react'
import { useConversation } from '@elevenlabs/react'
import ConversationalAIService from '@forwardflow/services/ConversationalAI'
import { ITranscriptLine } from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'
import { errorText } from '@forwardflow/features/campaign-flow/utils/errors'

export type TVoiceCallStatus =
   'idle' | 'starting' | 'live' | 'ended' | 'unavailable'

/**
 * A real voice call in the browser for "Try it as a lead": the dashboard's
 * ElevenLabs conversational agent (the one the assistants page talks to),
 * given this playbook's words as its prompt and a stand-in voice. The user
 * hears it and answers through the microphone; the transcript comes back
 * line by line. When it cannot start (no key, no microphone, offline) the
 * phone falls back to the typed rehearsal and says why.
 */
export const useVoiceCall = () => {
   const [status, setStatus] = useState<TVoiceCallStatus>('idle')
   const [error, setError] = useState<string | null>(null)
   const [lines, setLines] = useState<ITranscriptLine[]>([])
   const stream = useRef<MediaStream | null>(null)
   const stopMic = () => {
      stream.current?.getTracks().forEach((t) => t.stop())
      stream.current = null
   }
   const conversation = useConversation({
      onConnect: () => setStatus('live'),
      onDisconnect: () => {
         stopMic()
         setStatus((s) => (s === 'live' || s === 'starting' ? 'ended' : s))
      },
      onMessage: ({ message, source }) =>
         setLines((l) => [
            ...l,
            { from: source === 'user' ? 'lead' : 'assistant', text: message },
         ]),
      onError: (e) => {
         stopMic()
         setError(errorText(e, 'The voice call dropped.'))
         setStatus('unavailable')
      },
   })

   /*
    * The signed URL is fetched ahead of the call (when the phone opens), so
    * Accept goes straight to the microphone and the session: the browser only
    * lets audio start close to the click that asked for it.
    */
   /*
    * The agent's credentials are fetched ahead of the call (when the phone
    * opens), so Accept goes straight to the microphone and the session: the
    * browser only lets audio start close to the click that asked for it.
    * WebRTC is preferred: the socket transport needs an audio worklet that
    * some browsers never finish loading. A token lasts minutes, so one older
    * than five is fetched again.
    */
   type TTicket = { conversationToken: string } | { signedUrl: string }
   const ticket = useRef<{ at: number; value: Promise<TTicket> } | null>(null)
   const prefetch = useCallback(() => {
      if (ticket.current && Date.now() - ticket.current.at < 5 * 60000)
         return ticket.current.value
      const unwrap = <T>(answer: unknown, key: string): T | undefined => {
         const a = answer as Record<string, unknown> | null
         return (a?.[key] ??
            (a?.data as Record<string, unknown> | undefined)?.[key]) as
            T | undefined
      }
      const value: Promise<TTicket> =
         ConversationalAIService.getConversationToken()
            .then((answer) => {
               const conversationToken = unwrap<string>(answer, 'token')
               if (!conversationToken) throw new Error('no token')
               return { conversationToken } as TTicket
            })
            .catch(async () => {
               const signedUrl = unwrap<string>(
                  await ConversationalAIService.getConversationalAISignedUrl(),
                  'signed_url'
               )
               if (!signedUrl)
                  throw new Error('No voice agent is set up for this account.')
               return { signedUrl } as TTicket
            })
      value.catch(() => {
         ticket.current = null
      })
      ticket.current = { at: Date.now(), value }
      return value
   }, [])

   const start = useCallback(
      async (o: { prompt: string; voiceId: string }) => {
         setStatus('starting')
         setLines([])
         setError(null)
         try {
            const credentials = await prefetch()
            stream.current = await navigator.mediaDevices.getUserMedia({
               audio: true,
            })
            /* A call that has not connected in 20 s is given up, so the phone never hangs on "Connecting". */
            const watchdog = window.setTimeout(() => {
               setStatus((st) => {
                  if (st !== 'starting') return st
                  void conversation.endSession().catch(() => undefined)
                  stopMic()
                  setError('The voice call did not connect.')
                  return 'unavailable'
               })
            }, 20000)
            await conversation.startSession({
               ...credentials,
               connectionType:
                  'conversationToken' in credentials ? 'webrtc' : 'websocket',
               overrides: {
                  agent: { prompt: { prompt: o.prompt }, language: 'en' },
                  tts: { voiceId: o.voiceId },
               },
            })
            window.clearTimeout(watchdog)
            ticket.current = null
         } catch (e) {
            stopMic()
            setError(
               /NotAllowed|Permission|denied/i.test(
                  String((e as Error)?.name || e)
               )
                  ? 'Allow the microphone to talk on the test call.'
                  : errorText(e, 'The voice call could not start.')
            )
            setStatus('unavailable')
         }
      },
      [conversation, prefetch]
   )

   const end = useCallback(async () => {
      try {
         await conversation.endSession()
      } catch {
         /* already closed */
      }
      stopMic()
      setStatus((s) => (s === 'idle' ? s : 'ended'))
   }, [conversation])

   return {
      status,
      error,
      lines,
      speaking: conversation.isSpeaking,
      prefetch,
      start,
      end,
   }
}
